import React, { useMemo, useState, useCallback } from "react";
import { Target, CheckCircle2, AlertTriangle, Ban, EyeOff } from "lucide-react";
import { FORFETTARIO_THRESHOLD, FORFETTARIO_THRESHOLD_EXIT } from "../../constants/ateco";

function formatCurrency(value) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(value) || 0);
}

function formatDateIT(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

// Soglia di rilevanza: sotto questo potenziale la card resta nascosta.
const SHOW_MIN = 70000;

// Quick action "escludi le vecchie": fatture scadute da più di N giorni.
const GIORNI_VECCHIA = 90;

const STATI = {
  ok:      { label: "Incassabile", emoji: "✅", Icon: CheckCircle2, light: "text-emerald-700 bg-emerald-50", dark: "text-emerald-300 bg-emerald-900/30" },
  over85:  { label: "Supera 85k",  emoji: "⚠️", Icon: AlertTriangle, light: "text-amber-700 bg-amber-50",   dark: "text-amber-300 bg-amber-900/30" },
  over100: { label: "Supera 100k", emoji: "🛑", Icon: Ban,           light: "text-red-700 bg-red-50",       dark: "text-red-300 bg-red-900/30" },
  escluso: { label: "Esclusa",     emoji: "➖", Icon: EyeOff,        light: "text-neutral-600 bg-neutral-100", dark: "text-neutral-400 bg-neutral-800" },
};

// Data di riferimento della fattura (scadenza, con fallback all'emissione).
function dataRiferimento(inv) {
  return inv.dueDate || inv.issueDate || "";
}

// true se la fattura è scaduta da più di `giorni` giorni.
export function isScadutaDaOltre(inv, giorni = GIORNI_VECCHIA) {
  const d = dataRiferimento(inv);
  if (!d) return false;
  const limite = new Date();
  limite.setDate(limite.getDate() - giorni);
  return new Date(d) < limite;
}

// Classifica il totale progressivo rispetto alle soglie forfettario.
// Le soglie sono parametriche: nel primo anno arrivano ragguagliate ai giorni di attività.
export function classificaCumulato(cumulato, soglia = FORFETTARIO_THRESHOLD, sogliaExit = FORFETTARIO_THRESHOLD_EXIT) {
  if (cumulato <= soglia) return "ok";
  if (cumulato <= sogliaExit) return "over85";
  return "over100";
}

// Costruisce le righe di pianificazione: fatture aperte (importo > 0), per scadenza,
// con totale progressivo a partire dagli incassi già realizzati nell'anno.
// Le fatture in `excludedIds` restano in lista (per poterle riattivare) ma NON
// alimentano il cumulato: sono escluse dalla simulazione.
export function buildPlanningRows(
  invoices,
  incassiAnno,
  soglia = FORFETTARIO_THRESHOLD,
  sogliaExit = FORFETTARIO_THRESHOLD_EXIT,
  excludedIds = null
) {
  const unpaid = (invoices || [])
    .filter((inv) => !inv.paid && (Number(inv.grossAmount) || 0) > 0)
    .sort((a, b) => dataRiferimento(a).localeCompare(dataRiferimento(b)));

  let running = Number(incassiAnno) || 0;
  const rows = unpaid.map((inv) => {
    const escluso = Boolean(excludedIds && excludedIds.has(inv.id));
    if (escluso) {
      // Non accumula: il cumulato resta quello dell'ultima fattura inclusa.
      return { inv, cumulato: running, stato: "escluso", escluso: true };
    }
    running += Number(inv.grossAmount) || 0;
    return { inv, cumulato: running, stato: classificaCumulato(running, soglia, sogliaExit), escluso: false };
  });
  return rows;
}

export default function SogliaPlanningCard({
  invoices,
  incassiAnno,
  selectedYear,
  soglia = FORFETTARIO_THRESHOLD,
  sogliaExit = FORFETTARIO_THRESHOLD_EXIT,
  darkMode = false,
}) {
  const currentYear = new Date().getFullYear();

  // Simulazione locale (non persistita): quali fatture NON considerare nella proiezione.
  const [excludedIds, setExcludedIds] = useState(() => new Set());

  const { rows, visible } = useMemo(() => {
    const rows = buildPlanningRows(invoices, incassiAnno, soglia, sogliaExit, excludedIds);
    // NB: la visibilità si basa sul potenziale TOTALE (non sulle sole selezionate),
    // altrimenti deselezionando tutto la card sparirebbe e non si potrebbe più riattivare.
    const sommaNonPagate = rows.reduce((s, r) => s + (Number(r.inv.grossAmount) || 0), 0);
    const potenziale = (Number(incassiAnno) || 0) + sommaNonPagate;
    const visible = selectedYear === currentYear && rows.length > 0 && potenziale >= SHOW_MIN;
    return { rows, visible };
  }, [invoices, incassiAnno, selectedYear, currentYear, soglia, sogliaExit, excludedIds]);

  const toggleOne = useCallback((id) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selezionaTutte = useCallback(() => setExcludedIds(new Set()), []);

  const deselezionaTutte = useCallback(() => {
    setExcludedIds(new Set(rows.map((r) => r.inv.id)));
  }, [rows]);

  const escludiVecchie = useCallback(() => {
    setExcludedIds(new Set(rows.filter((r) => isScadutaDaOltre(r.inv)).map((r) => r.inv.id)));
  }, [rows]);

  // Totali della simulazione.
  const incluse = rows.filter((r) => !r.escluso);
  const escluse = rows.filter((r) => r.escluso);
  const sommaIncluse = incluse.reduce((s, r) => s + (Number(r.inv.grossAmount) || 0), 0);
  const sommaEscluse = escluse.reduce((s, r) => s + (Number(r.inv.grossAmount) || 0), 0);
  const proiezione = (Number(incassiAnno) || 0) + sommaIncluse;
  const nVecchie = rows.filter((r) => isScadutaDaOltre(r.inv)).length;

  if (!visible) return null;

  const hasRischio = incluse.some((r) => r.stato !== "ok");
  const card = darkMode ? "bg-neutral-900 border-neutral-800" : "bg-white border-neutral-200";
  const th = `px-3 py-2 text-left font-medium ${darkMode ? "text-neutral-400" : "text-neutral-500"}`;
  const btn = `px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
    darkMode
      ? "border-neutral-700 text-neutral-300 hover:bg-neutral-800"
      : "border-neutral-200 text-neutral-700 hover:bg-neutral-50"
  }`;

  return (
    <section className={`mt-6 rounded-xl border p-5 ${card}`}>
      <div className="flex items-center gap-2 mb-1">
        <Target className={`w-5 h-5 ${darkMode ? "text-forfy-300" : "text-forfy-600"}`} />
        <h2 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? "text-white" : "text-neutral-900"}`}>
          Pianificazione incassi — soglia forfettario
        </h2>
      </div>
      <p className={`text-sm ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
        Già incassato nel {selectedYear}: <strong>{formatCurrency(incassiAnno)}</strong> — soglia {formatCurrency(soglia)}.
        Seleziona le fatture da includere nella proiezione.
      </p>
      <p className={`mt-1 text-xs ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
        È una simulazione: i KPI in alto continuano a mostrare la situazione reale con tutte le fatture.
      </p>

      {/* Quick actions */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={selezionaTutte} className={btn}>Seleziona tutte</button>
        <button type="button" onClick={deselezionaTutte} className={btn}>Deseleziona tutte</button>
        {nVecchie > 0 && (
          <button type="button" onClick={escludiVecchie} className={btn}>
            Escludi scadute oltre {GIORNI_VECCHIA} giorni ({nVecchie})
          </button>
        )}
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className={darkMode ? "bg-neutral-800/50" : "bg-neutral-50"}>
              <th className={`${th} w-10`}><span className="sr-only">Includi</span></th>
              <th className={th}>Fattura</th>
              <th className={th}>Cliente</th>
              <th className={`${th} text-right`}>Importo</th>
              <th className={th}>Scadenza</th>
              <th className={`${th} text-right`}>Se incassi…</th>
              <th className={th}>Stato</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ inv, cumulato, stato, escluso }) => {
              const s = STATI[stato];
              return (
                <tr
                  key={inv.id}
                  className={`border-t ${darkMode ? "border-neutral-800" : "border-neutral-100"} ${escluso ? "opacity-50" : ""}`}
                >
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={!escluso}
                      onChange={() => toggleOne(inv.id)}
                      aria-label={`Includi la fattura ${inv.invoiceCode || ""} nella proiezione`}
                      className="rounded"
                    />
                  </td>
                  <td className={`px-3 py-2.5 ${darkMode ? "text-neutral-300" : "text-neutral-700"}`}>{inv.invoiceCode || "—"}</td>
                  <td className={`px-3 py-2.5 ${darkMode ? "text-neutral-300" : "text-neutral-700"}`}>{inv.clientName || "—"}</td>
                  <td className={`px-3 py-2.5 text-right tabular-nums ${escluso ? "line-through" : ""} ${darkMode ? "text-neutral-300" : "text-neutral-700"}`}>
                    {formatCurrency(inv.grossAmount)}
                  </td>
                  <td className={`px-3 py-2.5 ${darkMode ? "text-neutral-400" : "text-neutral-500"}`}>{formatDateIT(dataRiferimento(inv))}</td>
                  <td className={`px-3 py-2.5 text-right tabular-nums font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
                    {escluso ? "—" : <>Tot: {formatCurrency(cumulato)}</>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${darkMode ? s.dark : s.light}`}>
                      <s.Icon className="w-3.5 h-3.5" /> {s.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Riepilogo selezione */}
      <div className={`mt-4 rounded-lg p-3 text-sm ${darkMode ? "bg-neutral-800/50 text-neutral-300" : "bg-neutral-50 text-neutral-700"}`}>
        <div>
          Incassi previsti: <strong>{formatCurrency(proiezione)}</strong>{" "}
          ({incluse.length} {incluse.length === 1 ? "fattura selezionata" : "fatture selezionate"} su {rows.length})
        </div>
        {escluse.length > 0 && (
          <div className={`mt-1 text-xs ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
            Escluse: {escluse.length} {escluse.length === 1 ? "fattura" : "fatture"} ({formatCurrency(sommaEscluse)})
          </div>
        )}
      </div>

      <p className={`mt-3 text-xs ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
        {hasRischio
          ? <>Per restare nel forfettario nel {selectedYear}, valuta di rinviare l'incasso delle fatture ⚠️ / 🛑 al prossimo anno. </>
          : <>Con le fatture selezionate resti sotto la soglia. </>}
        È solo informativo: la scelta di incassare resta tua. Oltre {formatCurrency(sogliaExit)} l'uscita è immediata con IVA.
      </p>
    </section>
  );
}
